import React from 'react';

import { Pill } from '../../design-system/components/desktop';
import {
  CartLine,
  CartLines,
  CartSummary,
  SummaryCount,
  SummaryRow,
  SummaryTotal,
} from '../../design-system/patterns/cart';
import { formatCurrency } from '../../utils/common';
import { isPriceUnavailable, PRICE_UNAVAILABLE_PHRASE } from '../../utils/priceAvailability';

/**
 * The checkout order summary. The THIRD of the four surfaces that show a cart
 * line, and it uses the same `CartLine` as the cart page and the cart drawer,
 * in `readOnly` mode: no stepper, no remove, quantity folded into the price.
 *
 * SHIPPING, OPEN PRODUCT QUESTION 1, READ THIS BEFORE CHANGING THE ARITHMETIC
 * ---------------------------------------------------------------------------
 * The standing product rule is that Sleevr never shows a shipping price,
 * because it cannot pull courier data. Checkout is the one place that breaks
 * it: `cartModule.getRate` returns a rate (R135 in the current fixtures), the
 * app renders it, and `total = subtotal + shipping` depends on it. Resolving
 * that contradiction is a product decision and is NOT this conversion's to
 * make, so the arithmetic and the row are preserved EXACTLY as they were:
 *
 *   the Shipping row renders only when `shipping > 0`, which is precisely the
 *   old condition, and it is a bare figure. No courier is named, no origin
 *   city is named. On the in-person path `shipping` is 0 and the row is gone.
 *
 * See "Open product questions" 1 in `design-system/CONVERSION-MAP.md`.
 *
 * DISCOUNT is a real, server-side amount. It is NOT a promo code: this app has
 * no code-redemption feature anywhere and no input for one may be added here.
 * The row renders only when there is an amount to render.
 *
 * TASK 11: A ROW CAN BE BLOCKED. Either its OWN fresh data says its market
 * price could not be retrieved (`isPriceUnavailable`, read live off
 * `listing.pricing_mode`/`price_stale` -- never persisted, so a row that
 * refreshes clean un-blocks itself for free), or `blockedListingIds` names it
 * -- the ids `create-order/` rejected on a prior submit
 * (`unavailable_listing_ids`, arriving as STRINGS; `Checkout.jsx` coerces
 * before building this Set, and this file coerces `listing.id` to match it).
 * This is the AFFORDANCE, not the guarantee: the server-side 400 is what
 * actually protects the order, this only tells the buyer why before they
 * try again.
 */
const OrderSummary = ({ checkoutData, blockedListingIds, shipping = 0, discount = 0 }) => {
  const items = checkoutData?.items || [];

  const subtotal = items.reduce((sum, item) => sum + item.card_listing.price * item.quantity, 0);
  const total = subtotal + shipping - discount;

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    /* NO `sticky` HERE, and it is not an oversight. On /cart the summary IS the
       grid item, so pinning it is correct. On checkout it is the first of two
       children — the Place Order block follows it — and a sticky first child
       slides down over its own sibling. `.dsa-checkout__aside` pins the whole
       column instead, and this card scrolls inside it. */
    <CartSummary title="Order summary">
      {/* NO `SummaryThumbs`. That strip stands in for the lines "above a
          CONDENSED summary" — it is the glance-preview you show INSTEAD of
          them. This summary is not condensed: it renders a full `CartLine`
          per item below, and `CartLine` already carries the cover, so the
          strip put every item's art on the page twice. */}
      <SummaryCount>
        {itemCount} {itemCount === 1 ? 'item' : 'items'}
      </SummaryCount>

      <CartLines>
        {items.map((item, index) => {
          const listing = item.card_listing;
          /* THE ID-TYPE TRAP: `blockedListingIds` holds STRINGS
             (`Checkout.jsx` coerces the server's `unavailable_listing_ids`
             with String() on the way in), while `listing.id` here is a
             number off `ListingsByIdsView`. Coerce before comparing --
             `Set.has` is strict-equality and a bare `listing.id` would
             silently never match. */
          const blocked =
            isPriceUnavailable(listing) || Boolean(blockedListingIds?.has(String(listing?.id)));
          return (
            <CartLine
              key={listing.id ?? index}
              compact
              readOnly
              image={listing.images?.[0]?.image}
              imageAlt={listing.card_name || ''}
              name={listing.card_name || listing.card?.name || 'Card'}
              condition={listing.condition ? <Pill fit>{listing.condition}</Pill> : null}
              unitPrice={formatCurrency(listing.price)}
              quantity={item.quantity}
              lineTotal={formatCurrency(listing.price * item.quantity)}
              unavailable={blocked}
              note={blocked ? <Pill tone="warning">{PRICE_UNAVAILABLE_PHRASE}</Pill> : null}
            />
          );
        })}
      </CartLines>

      <SummaryRow label="Subtotal" value={formatCurrency(subtotal)} />

      {shipping > 0 && (
        /* See the block comment above: this figure is the server's rate and the
           total depends on it. Open product question 1. */
        <SummaryRow label="Shipping" value={formatCurrency(shipping)} />
      )}

      {discount > 0 && <SummaryRow label="Discount" value={`-${formatCurrency(discount)}`} />}

      <SummaryTotal value={formatCurrency(total)} />
    </CartSummary>
  );
};

export default OrderSummary;
